import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Check, ShieldCheck, ShieldOff, Trash2, Clock } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "recruiter" });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const fmtLastLogin = (iso) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const day = 86400000;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < day) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const pending = users.filter((u) => !u.approved);
  const active = users.filter((u) => u.approved);

  const approve = async (uid) => { try { await api.patch(`/admin/users/${uid}`, { approved: true }); toast.success("User approved"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const setRole = async (uid, role) => { try { await api.patch(`/admin/users/${uid}`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const toggleActive = async (uid, active) => { try { await api.patch(`/admin/users/${uid}`, { active }); toast.success(active ? "Reactivated" : "Deactivated"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = async (uid) => { try { await api.delete(`/admin/users/${uid}`); toast.success("User removed"); setConfirmDelete(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const createUser = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Name, email and a 6+ char password are required"); return; }
    try { await api.post("/admin/users", form); toast.success("User created & approved"); setOpen(false); setForm({ name: "", email: "", password: "", role: "recruiter" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const Row = ({ u }) => (
    <tr data-testid={`user-row-${u.user_id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 shrink-0 rounded-full bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))] flex items-center justify-center text-xs font-semibold font-head">{(u.name || "?").trim().charAt(0).toUpperCase()}</span>
          <div className="min-w-0">
            <div className="font-medium truncate">{u.name}{u.user_id === user?.user_id && <span className="text-xs text-muted-foreground"> (you)</span>}</div>
            <div className="text-xs text-muted-foreground truncate">{u.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)} disabled={u.user_id === user?.user_id}>
          <SelectTrigger className="w-[130px] h-8" data-testid={`role-${u.user_id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="recruiter">Recruiter</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm" data-testid={`last-login-${u.user_id}`}>{fmtLastLogin(u.last_login)}</div>
        <div className="text-xs text-muted-foreground capitalize">{u.auth_provider || "password"}</div>
      </td>
      <td className="px-4 py-3">
        {!u.approved ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock size={11} className="mr-1" />Pending</Badge>
          : u.active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
          : <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">Deactivated</Badge>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {!u.approved && <Button size="sm" data-testid={`approve-${u.user_id}`} onClick={() => approve(u.user_id)} className="h-8 bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90"><Check size={14} className="mr-1" />Approve</Button>}
          {u.approved && u.user_id !== user?.user_id && (u.active
            ? <Button size="sm" variant="outline" data-testid={`deactivate-${u.user_id}`} onClick={() => toggleActive(u.user_id, false)} className="h-8"><ShieldOff size={14} className="mr-1" />Deactivate</Button>
            : <Button size="sm" variant="outline" data-testid={`reactivate-${u.user_id}`} onClick={() => toggleActive(u.user_id, true)} className="h-8"><ShieldCheck size={14} className="mr-1" />Reactivate</Button>)}
          {u.user_id !== user?.user_id && <button data-testid={`delete-user-${u.user_id}`} onClick={() => setConfirmDelete(u)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"><Trash2 size={15} /></button>}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between mb-8 fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--teal))] font-semibold">Access Control</div>
          <h1 className="font-head text-3xl font-semibold tracking-tight mt-1.5">Team & Access</h1>
          <p className="text-muted-foreground text-sm mt-1.5">Invite-only. New sign-ups stay locked until an administrator approves them.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="invite-user-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90 shadow-card"><UserPlus size={16} className="mr-1.5" />Add User</Button>
      </div>

      {pending.length > 0 && (
        <div className="mb-8">
          <h3 className="font-head font-semibold text-base mb-3 flex items-center gap-2"><Clock size={16} className="text-amber-600" /> Pending Approval <Badge variant="secondary">{pending.length}</Badge></h3>
          <div className="border border-amber-200 rounded-lg overflow-hidden bg-card shadow-card">
            <table className="w-full text-sm"><tbody>{pending.map((u) => <Row key={u.user_id} u={u} />)}</tbody></table>
          </div>
        </div>
      )}

      <h3 className="font-head font-semibold text-base mb-3">Team Members</h3>
      <div className="border border-border rounded-lg overflow-hidden bg-card shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>{["Member", "Role", "Last sign-in", "Status", ""].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>{active.map((u) => <Row key={u.user_id} u={u} />)}</tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Add User</DialogTitle>
          <DialogDescription>Creates a pre-approved account. Share the credentials with the new team member.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" className="mt-1" /></div>
            <div><Label className="text-xs">Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="invite-password" className="mt-1" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="recruiter">Recruiter</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createUser} data-testid="create-user-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head">Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {confirmDelete?.email} and revokes their access. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={() => remove(confirmDelete.user_id)} className="bg-destructive hover:bg-destructive/90">Remove user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
